import _ from "underscore";
import * as logger from "../utils/logger";
import DriveView, {IDriveViewOptions} from "../DriveView";
import Notifications, { UNCERTIFIED_HOS_NOTIFICATION_STRING, isNotificationDismissedRecently } from "../utils/notifications";
import Api from "../services/api";
import HosProcess from "../services/hosProcess";
import AssetsView from "./assets/main";
import SelectVehicleView from "./assets/selectVehicle";
import SelectTrailerView from "./assets/selectTrailer";
import SelectShipmentView from "./assets/selectShipment";
import ChangeHOSStatusView from "./hos/tabs/main/logout";
import DecideDvirView from "./dvir/main";
import InspectDvirView from "./dvir/inspect";
import LogsView from "./hos/tabs/logs/logs";
import UnassignedLogsView from "./hos/tabs/logs/logs.unassigned";
import ViewHosView from "./hos/tabs/logs/view";
import {TrailerAttachments} from "../model/TrailerAttachments";
import {dispatcher, DispatcherEvents} from "../utils/dispatcher";
import {getCurrentVehicle, isNoVehicle, setCurrentVehicle} from "../utils/device";
import {getHomeHash} from "../utils/uri";
import {extend} from "../utils/vanilla";
import rison from "rison";
import toastr from "../utils/toastr";
import {DriverActionTypes, PageLevel} from "../store/mobile/types";
import {MobileActions} from "../store/mobile/actions";
import {DataActions} from "../store/data/actions";
import {WorkdayType} from "../store/data/types";
import {SyncType} from "../sync/sync.types";
import {getActiveShipments} from "../model/Shipments";
import DVIRLog from "../model/DVIRLog";
import HosView from "./hos/tabs/main/main";
import AddLogView from "./hos/tabs/logs/addLog";

import pageHTML from "./workflow.tmpl";
import { hasExemption } from "hos-tools";
import { handleApi } from "../utils/error";
import { OpenCabWorkflowActions } from "../store/openCabWorkflow/actions";
import { openExternalUrl } from "../utils/native/OpenExternalUrl";
import { Dialog } from "./framework/dialog";
import LogsReviewChangesView from "./hos/tabs/logs/logsReviewChanges";
import { IReviewChangesParams } from "./hos/tabs/logs/editPcOrYmUtils";
import PerformanceProfilerFactory from "../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import { deferUntilIdle } from "../utils/deferUntilIdle";
import { isVerboseLogging } from "../utils/logger.internal";
import { getUploadChunkSize } from "../sync/eventLogs";
import { MINUTES } from "../constants";
import { getServerDate } from "../utils/storage/date";
import { uploadEventLogsFromIndexedDb } from "../sync/eventLogs";

const STEPS_WITH_BACK_BTN: TWorkflowStep[] = ["doDVIR", "selectTrailer", "selectShipment", "acceptLog", "addLog"];

export type TWorkflowStep = "selectVehicle" | "showAssets" | "selectTrailer" | "selectShipment" | "showAvailability" | "addLog" | "assignUnassigned"
                            | "checkPostTrip" | "decideDVIR" | "doDVIR" | "verifyLogs" | "acceptLog" | "reviewLogChanges" | "changeStatus" | "exit";

export interface IWorkflowState {
    next?: string;
    step?: TWorkflowStep;
    coDriver?: boolean;
    changeVehicle?: boolean;
    newDeviceId?: string;
    logId?: string;
    violations?;
    isLogout?: boolean;
    dvirId?: string;
    isFirstDvir?: boolean;
    completeDvirIds?: string[];
    newLogId?: string;
    skipWorkflow?: boolean;
    vehicleHint?: string;
    redirect?: string;
    newSdcDateTime?: string;
}

export const syncTypesForWorkflow: SyncType[] = [
    SyncType.HOS,
    SyncType.DVIR,
    SyncType.TrailerAttachments,
    SyncType.Shipments,
    SyncType.UserHosRuleSet,
    SyncType.DriverChange,
    SyncType.Media
];

export default class WorkflowView extends DriveView<Backbone.Model> {
    protected strings = [
        "You are being redirected to an external site: {externalSource}"
    ];

    private readonly template = _.template(pageHTML);
    private readonly api: Api;
    private readonly hosUtils: HosProcess;
    private readonly nextPage: string | undefined;
    private readonly step: TWorkflowStep;
    private readonly coDriver: boolean;
    private readonly changeVehicle: boolean; // Only used to determine what to navigate the user back to in getWorkflowPageLevel.
    private readonly newDeviceId: string | null;
    private readonly logId: string | null;
    private readonly violations: any[];
    private readonly dvirId: string | undefined;
    private readonly isFirstDvir: boolean;
    private readonly isLogout: boolean;
    private readonly completeDvirIds: string[];
    private readonly newLogId?: string;
    private readonly skipWorkflow?: boolean;
    private readonly vehicleHint?: string;
    private readonly redirect?: string;
    private readonly newSdcDateTime?: string;
    private currentView;
    private doneCallback: (...args) => void;

    constructor(options: IDriveViewOptions<Backbone.Model>) {
        super(options);
        this.api = this.app.api;
        this.hosUtils = this.app.hosUtils;
        this.currentView = null;

        let state: Partial<IWorkflowState> = options.state;
        if (!state) {
            this.navigateToNextWorkflowPage();
            return;
        }

        this.nextPage = state.next;
        this.step = state.step || "selectVehicle";
        this.isLogout = state.isLogout || false;
        this.changeVehicle = state.changeVehicle || false;
        this.coDriver = state.coDriver || false;
        this.skipWorkflow = state.skipWorkflow || false;
        this.newDeviceId = state.newDeviceId || null;
        this.logId = state.logId || null;
        this.violations = state.violations || null;
        this.dvirId = state.dvirId;
        this.isFirstDvir = state.isFirstDvir || false;
        this.completeDvirIds = state.completeDvirIds || [];
        this.newLogId = state.newLogId;
        this.vehicleHint = state.vehicleHint;
        this.redirect = state.redirect;
        this.newSdcDateTime = state.newSdcDateTime;

        // Trigger workflow only if we're entering for the first time
        if (!this.app.store.getState().mobile.driverActionNecessary) {
            this.dispatchDriverActionNecessary(true);
        }
        if (state.skipWorkflow) {
            if (getCurrentVehicle() === null) {
                setCurrentVehicle({ id: "NoDeviceId" });
                this.app.store.dispatch(OpenCabWorkflowActions.setSkippedAssetSelection(true));
            }
            this.navigateOutOfWorkflow();
        }
    }

    dispatchDriverActionNecessary(isNecessary: boolean) {
        this.app.store.dispatch(MobileActions.setDriverActionNecessary({
            isNecessary: isNecessary,
            actionType: DriverActionTypes.Workflow
        }));
    }

    getNextPage() {
        return this.nextPage || getHomeHash();
    }

    getWorkflowStateObj(additionalProperties?) {
        let defaultProperties = {
            app: this.app,
            workflow: true,
            pageLevel: this.getWorkflowPageLevel()
        };

        return !additionalProperties ? defaultProperties : extend({}, defaultProperties, additionalProperties);
    }

    navigateToNextWorkflowPage (nextStep?: TWorkflowStep, state: IWorkflowState = {}) {
        if (state && this.nextPage) {
            state.next = this.nextPage;
        }

        if (state && this.redirect) {
            state.redirect = this.redirect;
        }

        state.isLogout = this.isLogout || false;
        state.changeVehicle = this.changeVehicle || false;
        state.coDriver = this.coDriver || false;
        state.step = nextStep || "selectVehicle";

        this.app.router.navigate(`workflow,${ (state ? rison.encode(state) : "") }`, true);
    }

    render() {
        void this.display(this.template())
        .then(() => {
            let currentUser = this.app.userService.getCurrentUser();
            if (!currentUser) {
                // Logout if there is no active user
                this.navigateOutOfWorkflow(true);
            }
            let stepHandlers = {
                "selectVehicle": () => {
                    this.selectVehicle(currentUser as IUser);
                    this.createView();
                },
                "showAssets": () => {
                    if (!isNoVehicle() && (this.app.securityService.can("viewTrailers") || this.app.securityService.can("viewShipments")) && (!this.isLogout || this.hasTrailersOrShipments())) {
                        this.showAssets();
                        this.createView();
                    } else {
                        this.navigateToNextWorkflowPage(this.isLogout ? "verifyLogs" : "showAvailability");
                    }
                },
                "selectTrailer": () => {
                    if (this.app.securityService.can("viewTrailers") && (!this.isLogout || this.countAttachedTrailers() > 0)) {
                        this.selectTrailer();
                        this.createView();
                    } else {
                        this.navigateToNextWorkflowPage("showAssets");
                    }
                },
                "selectShipment": () => {
                    if (this.app.securityService.can("viewShipments") && (!this.isLogout || this.countActiveShipments() > 0)) {
                        this.selectShipment();
                        this.createView();
                    } else {
                        this.navigateToNextWorkflowPage("showAssets");
                    }
                },
                "showAvailability": () => {
                    if (!this.app.securityService.can("useHos")) {
                        if (this.coDriver) {
                            this.navigateOutOfWorkflow();
                        } else {
                            this.navigateToNextWorkflowPage("decideDVIR");
                        }
                    } else {
                        if (!this.changeVehicle && this.shouldShowAvailabilityWarning()) {
                            this.showAvailability();
                            this.createView();
                        } else {
                            this.navigateToNextWorkflowPage("verifyLogs");
                        }
                    }
                },
                "addLog": () => {
                    this.addLog();
                    this.createView();
                },
                "assignUnassigned": () => {
                    if (this.app.securityService.canAssignUnassignedLogsForWorkflow()) {
                        this.checkIfUnassignedLogs();
                    } else if (this.coDriver) {
                        this.navigateOutOfWorkflow();
                    } else {
                        this.navigateToNextWorkflowPage("decideDVIR");
                    }
                },
                "checkPostTrip": () => {
                    if (this.isLogout) {
                        // First step that occurs after selecting logout from the topbar
                        if (currentUser) {
                            // Start uploading event logs optimistically in the background while the user is going through the workflow
                            const chunkSize = getUploadChunkSize();
                            // Upload as many logs as possible while the user is going through the workflow by passing `-1` for `maxUploadLoops`
                            // The final upload will occur in session.close(), where it will be bounded by 10 loops to ensure it doesn't run indefinitely in case of unforeseen issues
                            uploadEventLogsFromIndexedDb(this.api, chunkSize, -1);
                        }
                        if (currentUser && this.app.securityService.can("attemptHos") && this.app.store.getState().data.ELDJurisdiction !== "EWD") {
                            this.showEldStatusToast(currentUser);
                        }
                        if (this.app.securityService.canUseDvirForWorkflow()) {
                            this.checkIfPostTrip();
                        } else {
                            this.navigateToNextWorkflowPage("showAssets");
                        }
                    } else {
                        // shouldn't be here
                        this.navigateOutOfWorkflow();
                    }
                },
                "decideDVIR": () => {
                    if (this.app.securityService.canUseDvirForWorkflow()) {
                        this.decideDVIR();
                        this.createView();
                    } else {
                        if (this.isLogout) {
                            this.navigateToNextWorkflowPage("showAssets");
                        } else {
                            this.navigateOutOfWorkflow();
                        }
                    }
                },
                "doDVIR": () => {
                    this.doDVIR();
                    this.createView();
                },
                "verifyLogs": () => {
                    if (this.app.securityService.canVerifyLogsForWorkflow(this.changeVehicle)) {
                        this.checkIfUnverifiedLogs();
                    } else {
                        if (this.isLogout) {
                            this.navigateOutOfWorkflow(true);
                        } else if (isNoVehicle()) {
                            // Do not do DVIR or claim unassigned logs if no vehicle
                            this.navigateOutOfWorkflow();
                        } else {
                            this.navigateToNextWorkflowPage("assignUnassigned");
                        }
                    }
                },
                "acceptLog": () => {
                    this.acceptLog();
                    this.createView();
                },
                 "reviewLogChanges": () => {
                    this.reviewLogChanges();
                    this.createView();
                },
                "changeStatus": () => {
                    if (this.app.securityService.can("useHos") || (this.app.securityService.can("attemptHos") && isNoVehicle()) && !this.changeVehicle) {
                        this.changeStatus();
                        this.createView();
                    } else {
                        this.navigateOutOfWorkflow(true);
                    }
                }
            };

            let handler = stepHandlers[this.step];
            handler && handler.call(this);
        });

        return this;
    }

    createView() {
        const view = this.currentView;
        view.translate().then(() => {
            // Guard against stale view: if currentView changed during translate(), abort
            if (this.currentView !== view) {
                return;
            }
            view.setElement(document.querySelector("div#currentPanel")).render();
            view.listenTo(dispatcher, DispatcherEvents.CompleteWorkflowStep, this.doneCallback);
        });
    }

    selectVehicle (currentUser: IUser) {
        // We haven't selected a vehicle so don't know if hos is enabled for the vehicle, instead use attempt
        if (this.app.securityService.can("attemptHos") && !this.changeVehicle && this.app.store.getState().data.ELDJurisdiction !== "EWD") {
            this.showEldStatusToast(currentUser);
        }

        this.currentView = new SelectVehicleView(
            this.getWorkflowStateObj({
                isInitialSelect: !this.changeVehicle,
                changeVehicle: this.changeVehicle,
                newDeviceId: this.newDeviceId,
                vehicleHint: this.vehicleHint
            })
        );
        this.doneCallback = (selectedVehicle: boolean | null) => this.navigateToNextWorkflowPage(selectedVehicle ? "showAssets" : "verifyLogs");
    }

    showAssets() {
        this.currentView = new AssetsView(this.getWorkflowStateObj({ logout: this.isLogout }));
        this.doneCallback = (step: TWorkflowStep) => {
            // `step` values currently passed by AssetView: "selectTrailer", "selectShipment"
            // When clicking on a button attach or detach a trailer or shipment, `step` is otherwise undefined"
            const nextStep = step || (this.isLogout ? "verifyLogs" : "showAvailability");

            if (!this.isLogout && nextStep === "showAvailability") {
                // If logging in, wait for any syncs to complete before showing availability screen
                this.waitForSyncsAndNavigateToNextWorkflowPage(nextStep);
                // Run rest of deferred non-urgent syncs right after we wait for the existing syncs to complete so we don't accidentally wait for them
                this.runDeferredNonUrgentSyncs();
            } else {
                this.runDeferredNonUrgentSyncs();
                this.navigateToNextWorkflowPage(nextStep);
            }
        };
    }

    selectTrailer () {
        this.currentView = new SelectTrailerView(this.getWorkflowStateObj());
        this.doneCallback = () => this.navigateToNextWorkflowPage(!this.isLogout || this.hasTrailersOrShipments() ? "showAssets" : "verifyLogs");
    }

    selectShipment () {
        this.currentView = new SelectShipmentView(this.getWorkflowStateObj());
        this.doneCallback = () => this.navigateToNextWorkflowPage(!this.isLogout || this.hasTrailersOrShipments() ? "showAssets" : "verifyLogs");
    }

    showAvailability() {
        this.currentView = new HosView(this.getWorkflowStateObj({ title: "Availability", newLogId: this.newLogId }));
        this.doneCallback = (add: boolean) => this.navigateToNextWorkflowPage(add ? "addLog" : "verifyLogs");
    }

    addLog() {
        this.currentView = new AddLogView(this.getWorkflowStateObj());
        this.doneCallback = (newLogId) => this.navigateToNextWorkflowPage("showAvailability", { newLogId });
    }

    assignUnassigned() {
        this.currentView = new UnassignedLogsView(this.getWorkflowStateObj({ changeVehicle: this.changeVehicle }));
        this.doneCallback = () => this.coDriver ? this.navigateOutOfWorkflow() : this.navigateToNextWorkflowPage("decideDVIR");
    }

    acceptLog() {
        const clickLogAction = (reviewLogChangesParams: IReviewChangesParams) => {
            this.navigateToNextWorkflowPage("reviewLogChanges", reviewLogChangesParams);
        };

        this.currentView = new ViewHosView(this.getWorkflowStateObj({
            clickLogAction,
            state: {
                id: this.logId,
                violations: this.violations
            }
        }));
        this.doneCallback = () => this.navigateToNextWorkflowPage("verifyLogs");
    }

    reviewLogChanges() {
        this.currentView = new LogsReviewChangesView(this.getWorkflowStateObj({
            state: {
                logId: this.logId,
                newSdcDateTime: this.newSdcDateTime,
                isRequestedEdit: true
            }
        }));
        this.doneCallback = () => this.navigateToNextWorkflowPage("verifyLogs");
    }

    verifyLogs() {
        const clickLogAction = (id, violations) => {
            this.navigateToNextWorkflowPage("acceptLog", {
                logId: id,
                violations
            });
        };

        let logsPageOptions = extend({}, { clickLogAction }, this.isLogout ? { loggingOut: true } : { loggingIn: true });
        this.currentView = new LogsView(this.getWorkflowStateObj(logsPageOptions));

        this.doneCallback = () => {
            if (this.isLogout) {
                this.navigateToNextWorkflowPage("changeStatus");
            } else {
                if (isNoVehicle()) {
                    this.navigateOutOfWorkflow();
                } else {
                    this.navigateToNextWorkflowPage("assignUnassigned");
                }
            }
        };
    }

    changeStatus () {
        this.currentView = new ChangeHOSStatusView(this.getWorkflowStateObj());
        this.doneCallback = (isLogout: boolean) => this.navigateOutOfWorkflow(isLogout);
    }

    decideDVIR() {
        this.currentView = new DecideDvirView(this.getWorkflowStateObj({
            changeVehicle: this.changeVehicle,
            logout: this.isLogout,
            completeDvirIds: this.completeDvirIds
        }));
        this.doneCallback = (dvirId, isFirstDvir) => {
            if (dvirId) {
                this.navigateToNextWorkflowPage("doDVIR", {
                    dvirId,
                    isFirstDvir,
                    completeDvirIds: this.completeDvirIds
                });
            } else {
                if (this.isLogout) {
                    this.navigateToNextWorkflowPage("showAssets");
                } else {
                    this.syncAfterDvir().then(() => {
                        this.navigateOutOfWorkflow();
                    });
                }
            }
        };
    }

    doDVIR() {
        this.currentView = new InspectDvirView(this.getWorkflowStateObj({ state: this.isFirstDvir ? { assetId: this.dvirId } : { id: this.dvirId } }));
        this.doneCallback = (completeDvirId?: string, navigateToAssets?: boolean) => {
            if (completeDvirId) {
                this.completeDvirIds.push(completeDvirId);
            }

            if (navigateToAssets) {
                this.navigateToNextWorkflowPage("showAssets");
                return;
            }

            if (this.areAllDvirsComplete()) {
                if (this.isLogout) {
                    this.navigateToNextWorkflowPage("showAssets");
                } else {
                    const currentVehicle = getCurrentVehicle();
                    currentVehicle?.id && this.app.store.dispatch(OpenCabWorkflowActions.setPreTripDvirCompleteVehicleId(currentVehicle.id));
                    this.syncAfterDvir().then(() => {
                        this.navigateOutOfWorkflow();
                    });
                }
            } else {
                this.navigateToNextWorkflowPage("decideDVIR", { completeDvirIds: this.completeDvirIds });
            }
        };
    }

    checkIfUnassignedLogs () {
        if (this.app.dutyStatusLogService.isAnyLogUnassignedForWorkflow(this.app.hosUtils)) {
            this.assignUnassigned();
            this.createView();
        } else {
            if (this.coDriver) {
                this.navigateOutOfWorkflow();
            } else {
                this.navigateToNextWorkflowPage("decideDVIR");
            }
        }
    }

    shouldShowAvailabilityWarning() {
        let jurisdiction = this.app.store.getState().data.ELDJurisdiction;
        if (jurisdiction === "EWD") {
            return false;
        }
        let currentUser = this.app.userService.getCurrentUser();
        let latestLog = this.app.dutyStatusLogService.getLatestStatusLog();
        if ((!latestLog && !currentUser?.isExemptHOSEnabled) || (latestLog && latestLog.status === "PC_Exempted")) {
            return false;
        }

        if (this.newLogId || (!this.coDriver && currentUser?.isExemptHOSEnabled && !isNoVehicle() && !(latestLog && latestLog.isSituationalDrivingStatus()))) {
            return true;
        }

        if (latestLog) {
            if (latestLog.isOnDutyStatus()) {
                let ruleResult = this.hosUtils.process(true);
                return this.hosUtils.checkIfInOrNearViolation(ruleResult).length > 0 && !latestLog.isAutomaticDriving();
            } else {
                return this.hosUtils.checkIfBreakWillBeInterrupted().length > 0;
            }
        }
        return false;
    }

    /**
     * Eliminate intermediate logs from verification in DutyStatusLogs.getNotVerifiedLogs()
     * If on login, checks from 00:00 on current day, to eliminate verifying today's logs. On logout we display all.
     */
    checkIfUnverifiedLogs () {
        let hasUncertifiedLogs = this.app.dutyStatusLogService.isAnyLogUnverifiedForWorkflow(this.hosUtils, this.isLogout);
        this.updateUncertifiedHosNotification(hasUncertifiedLogs);

        if (hasUncertifiedLogs) {
            this.verifyLogs();
            this.createView();
        } else {
            if (this.isLogout) {
                this.navigateToNextWorkflowPage("changeStatus");
            } else {
                if (isNoVehicle()) {
                    this.navigateOutOfWorkflow();
                } else {
                    this.navigateToNextWorkflowPage("assignUnassigned");
                }
            }
        }
    }

    getLatestDvirLogs(assetIdsList: string[]) {
        let dvirLogCollection = this.app.dvirLogsService.getDvirLogs();
        return assetIdsList.map(assetId => this.app.dvirLogsService.getLatestLog(dvirLogCollection, assetId)) || [];
    }

    areAllDvirsComplete() {
        let assetIds: string[] = [];
        let trailers = this.getAttachedTrailers();
        if (trailers.length > 0) {
            assetIds = trailers.map(trailer => trailer.id);
        }

        let currentVehicle = getCurrentVehicle();
        if (currentVehicle) {
            assetIds.push(currentVehicle.id);
        }

        if (assetIds.length > 0) {
            let latestDvirs = this.getLatestDvirLogs(assetIds);
            for (let i = 0; i < latestDvirs.length; i++) {
                let lastDvir = latestDvirs[i];
                if (!lastDvir || !this.completeDvirIds.some(id => id === lastDvir?.id)) {
                    return false;
                }
            }
        }
        return true;
    }

    //TODO: This code looks buggy
    checkIfPostTrip () {
        let navigateToDvir = () => this.navigateToNextWorkflowPage("decideDVIR");
        let currentVehicle = getCurrentVehicle();
        let lastLog = currentVehicle && this.getLatestDvirLogs([currentVehicle.id])[0];

        if (!lastLog || !currentVehicle) {
            navigateToDvir();
        } else {
            this.api.call("Get", {
                typeName: "Trip",
                search: {
                    deviceSearch: { id: currentVehicle.id },
                    fromDate: lastLog.dateTime
                },
                resultsLimit: 1
            }, result => {
                let trips = result || [];
                if (trips.length > 0) {
                    navigateToDvir();
                } else {
                    this.navigateToNextWorkflowPage("showAssets");
                }
            }, err => {
                handleApi(err);

                // couldn't get trips, show the DVIR page just in case...
                navigateToDvir();
            });
        }
    }

    /**
     * 4.3.3.1.1 Configuration of available categories impacting driving time recording
     */

    showEldStatusToast (currentUser: DeepReadonly<IUser>) {
        // eld exemption short haul /sales
        let exemptionList: string[] = this.calculateExemption(currentUser);
        let exemptionListStr = exemptionList.length > 0 ? exemptionList.join(", ") : "None available";
        let message = `<div>
                            <span>Exemptions</span><span class="noTranslate">: ${ exemptionListStr }</span>
                        </div>`;

        void this.translator.translateTemplate(message).then(toastMessage => toastr.info(toastMessage, undefined, undefined, { preventDuplicates: true, positionClass: "toast-bottom-center" }));
    }

    calculateExemption(currentUser: DeepReadonly<IUser>) {
        let exemptionList: string[] = [];
        if (this.app.securityService.can("attemptHos")) {
            let canUseExemption = hasExemption(this.hosUtils.getLatestHosRuleSetIdShortened());
            if (currentUser.isPersonalConveyanceEnabled) {
                exemptionList.push("PC");
            }
            if (currentUser.isYardMoveEnabled) {
                exemptionList.push("YM");
            }
            if (canUseExemption) {
                exemptionList.push("ELD");
            }
            if (currentUser.isAdverseDrivingEnabled) {
                exemptionList.push("ADC");
            }
        }
        return exemptionList;
    }

    getAttachedTrailers () {
        let trailerAttachmentCollection = new TrailerAttachments(this.api, this.app.store);
        return trailerAttachmentCollection.getCurrentTrailers() || [];
    }

    countAttachedTrailers () {
        return this.getAttachedTrailers().length;
    }

    countActiveShipments() {
        let shipments = getActiveShipments(this.app.userService, this.app.store, false);
        return shipments.length;
    }

    getWorkflowPageLevel() {
        if (STEPS_WITH_BACK_BTN.indexOf(this.step) > -1) {
            return PageLevel.ASSET_SPECIFIC_PAGE;
        } else if (this.isLogout || this.coDriver) {
            // Cancel btn returns to main page
            return PageLevel.RETURN_DASHBOARD;
        } else if (this.changeVehicle) {
            // Cancel btn returns to assets page
            return PageLevel.RETURN_ASSETS;
        } else {
            let user = this.app.userService.getCurrentUser();
            if (user && this.app.store.getState().data.workday[user.id] === WorkdayType.Stopped) {
                return PageLevel.RETURN_DASHBOARD;
            }

            // Cancel btn logs user out
            return PageLevel.FORCE_LOGOUT;
        }
    }

    remove () {
        this.currentView && this.currentView.remove();
        super.remove();
        return this;
    }
    // Update bell notification for uncertified HOS logs on login/logout
    private updateUncertifiedHosNotification(hasUncertifiedLogs: boolean) {
        let currentUser = this.app.userService.getCurrentUser();
        if (currentUser?.id) {
            let userId = currentUser.id;
            let dismissedDate = this.app.store.getState().openCabWorkflow.userLastSeenUncertifiedHosNotificationDate?.[userId];
            let isDismissedRecently = isNotificationDismissedRecently(dismissedDate, getServerDate());
            logger.log(logger.Source.SYNC, `Checked for uncertified HOS logs | uncertified logs: ${ hasUncertifiedLogs }`, currentUser);

            if (!hasUncertifiedLogs) {
                Notifications.remove("uncertifiedHosLogs", userId);
                this.app.store.dispatch(OpenCabWorkflowActions.clearUserLastSeenUncertifiedHosNotificationDate(userId));
            } else if (!isDismissedRecently) {
                this.translator.translate(UNCERTIFIED_HOS_NOTIFICATION_STRING)
                    .then(translatedMessage => {
                        if (this.app.dutyStatusLogService.isAnyLogUnverifiedForWorkflow(this.hosUtils, this.isLogout)) {
                            Notifications.addUncertifiedHosLogsNotification(userId, currentUser, translatedMessage, () => {
                                this.app.store.dispatch(OpenCabWorkflowActions.setUserLastSeenUncertifiedHosNotificationDate(userId, getServerDate().toISOString()));
                            });
                        }
                    })
                    .catch(error => handleApi(error));
            }
        }
    }

    private navigateHelper (isLogout: boolean) {
        if (isLogout) {
            this.app.router.navigate(`login,${ rison.encode({ loggingOut: true }) }`, true);
        } else {
            if (this.nextPage) {
                // Push a history entry for the dashboard so the back button returns the user there
                window.history.pushState(null, "", `${ window.location.pathname }#${ getHomeHash() }`);
            }
            this.app.router.navigate(this.getNextPage(), true);
        }
    }

    private navigateOutOfWorkflow(isLogout = false) {
        !this.skipWorkflow && this.dispatchDriverActionNecessary(false);

        let workday: WorkdayType;
        if (isLogout) {
            workday = WorkdayType.Initializing;
        } else if (!this.isLogout || this.coDriver) {
            workday = WorkdayType.Started;
        } else {
            workday = WorkdayType.Stopped;
        }

        let user = this.app.userService.getCurrentUser();
        user && this.app.store.dispatch(DataActions.setWorkday({ [user.id]: workday }));
        if (this.redirect) {
            let deepLink: string = this.redirect;
            let redirectDialog = new Dialog(
                "redirect-dialog",
                `<span class=\"noTranslate\">
                    ${ this.getString("You are being redirected to an external site: {externalSource}").replace("{externalSource}", deepLink) }
                </span>`,
                this.app.context.translationService.getTranslator(),
                "Confirmation Required",
                [[
                    {
                        id: "cancel-redirect",
                        className: "button_secondary",
                        text: "Cancel",
                        callback: () => {
                            redirectDialog.remove();
                            this.navigateHelper(isLogout);
                        }
                    }, {
                        id: "continue-redirect",
                        text: "Continue",
                        callback: () => {
                            redirectDialog.remove();
                            openExternalUrl(deepLink, "_blank");
                            this.navigateHelper(isLogout);
                        }
                    }
                ]],
                {
                    closable: false,
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );
            redirectDialog.open();
        } else {
            this.navigateHelper(isLogout);
        }
    }

    private async syncAfterDvir() {
        // Wait for any existing syncs to complete before starting (this fire & forget) DVIR sync
        const waitForSyncsToCompleteProfiler = PerformanceProfilerFactory.getInstance(MeasureName.WaitForSyncsToCompleteInWorkflowBeforeDVIRSync);
        waitForSyncsToCompleteProfiler.start();
        await this.app.sync.waitForSyncsToComplete()
            .catch(err => logger.error(logger.Source.SYNC, `Error waiting for syncs to complete before DVIR sync in workflow: ${ err }`));
        waitForSyncsToCompleteProfiler.end();

        let dvirLogs: DVIRLog[] = this.app.dvirLogsService.getDvirLogs();
        if (dvirLogs.some(dvirLog => !!dvirLog.modified)) {
            void this.app.sync.synchronize(false, null, [SyncType.HOS, SyncType.DVIR]);
        }
    }

    private hasTrailersOrShipments() {
        let shipmentCount = this.countActiveShipments();
        let trailersCount = this.countAttachedTrailers();
        return shipmentCount > 0 || trailersCount > 0;
    }

    private waitForSyncsAndNavigateToNextWorkflowPage(nextStep?: TWorkflowStep, state: IWorkflowState = {}, timeoutMs = 3 * MINUTES) {
        const waitForSyncsToCompleteProfiler = PerformanceProfilerFactory.getInstance(MeasureName.WaitForSyncsToCompleteInWorkflow);
        waitForSyncsToCompleteProfiler.start();
        this.app.sync.waitForSyncsToComplete(timeoutMs)
            .then(() => {
                waitForSyncsToCompleteProfiler.end();
                this.navigateToNextWorkflowPage(nextStep, state);
            })
            .catch(err => {
                waitForSyncsToCompleteProfiler.end();
                logger.error(logger.Source.SYNC, `Error waiting for syncs to complete in workflow: ${ err }`);
                this.navigateToNextWorkflowPage(nextStep, state);
            });
    }

    private runDeferredNonUrgentSyncs() {
        if (!this.isLogout) {
            // Run deferred sync for non-workflow entities (Messages, Trips, etc.)
            deferUntilIdle(() => this.app.sync.synchronize(false, undefined, syncTypesForWorkflow, undefined, true), { timeout: 1000, isVerboseLoggingEnabled: isVerboseLogging() });
        }
    }
}
